/**
 * The three deposit flows the platform supports, and which of them can actually
 * be served right now.
 *
 * An operator switches each flow on or off in svc-setting's `finance.config`,
 * PER BRAND: the gateway stamps the player's own agent on the read, and
 * svc-setting merges that brand's overrides over the platform row. So the driver
 * the flows were specified against — which of them a country and its banking
 * rails are ready for — is answered per brand, and a brand that has set nothing
 * follows the platform value it always followed.
 *
 * Enabled is not the same as SERVABLE: a flow whose prerequisites are missing
 * has to say so, because the alternative is a screen that renders nothing and a
 * player who thinks deposits are broken.
 *
 * Pure on purpose — the screen owns the queries, this owns the rules.
 */

/** In the order the picker offers them, which is the order the flows were specified in. */
export const DEPOSIT_MODES = ['auto', 'gateway', 'slip'] as const;

export type DepositMode = (typeof DEPOSIT_MODES)[number];

/**
 * The `finance.config` fields, each OPTIONAL. A field is absent from a config
 * row written before it existed, and svc-setting merges its own default in on
 * read — but the SPA can hold a cached response older than that deploy, and
 * treating an absent switch as `false` there would take every deposit flow off
 * the screen at once.
 */
export interface DepositModeSwitches {
  depositModeAuto?: boolean;
  depositModeGateway?: boolean;
  depositModeSlip?: boolean;
}

/**
 * Mirrors svc-setting's own DEFAULTS — the floor under both the platform row and
 * a brand's overrides. Only the gateway flow is on: it is the one that files a
 * request an operator can act on, and the automatic flow's whole premise is that
 * transfers are credited without anyone watching a queue — something an operator
 * has to assert for the brand it is turning on.
 */
export const DEPOSIT_MODE_DEFAULTS: Required<DepositModeSwitches> = {
  depositModeAuto: false,
  depositModeGateway: true,
  depositModeSlip: false,
};

const SWITCH_OF: Record<DepositMode, keyof DepositModeSwitches> = {
  auto: 'depositModeAuto',
  gateway: 'depositModeGateway',
  slip: 'depositModeSlip',
};

/** The flows the operator has switched on, in picker order. May be empty. */
export function enabledDepositModes(config: DepositModeSwitches): DepositMode[] {
  return DEPOSIT_MODES.filter((mode) => {
    const key = SWITCH_OF[mode];
    return config[key] ?? DEPOSIT_MODE_DEFAULTS[key];
  });
}

/**
 * Why an enabled flow cannot be served.
 *
 * `noHouseAccount` — the operator exposes no deposit-capable account to this
 * member. The automatic flow IS the account: with none there is nothing on the
 * panel at all, and a player told to transfer with no destination is worse off
 * than one told to ask.
 *
 * `noSlipStorage` — private slip storage exists in the platform now, but a
 * given environment's object storage can still be genuinely unreachable (a
 * bucket not yet provisioned, credentials not yet rotated in). The screen only
 * learns that from a real attempt — svc-finance answering 503 `FIN-313` — so
 * this is a fact the caller reports after trying, not something computed
 * up front.
 */
export type DepositModeBlocker = 'noHouseAccount' | 'noSlipStorage';

export interface DepositModeContext {
  /** Deposit-capable house accounts visible to this member, once the fetch has resolved. */
  houseAccountCount: number;
  /**
   * Whether an upload attempt against this member's session has already come
   * back `FIN-313`. Absent/false reads as available — storage now genuinely
   * exists, so treating "not yet attempted" as "unavailable" would reproduce
   * the old always-off behaviour under a new name and hand the player a
   * notice for a fault that never happened.
   */
  slipStorageUnavailable?: boolean;
}

/**
 * The gateway flow is deliberately NOT blocked by an empty account list. Its
 * payload is the request itself — an exact figure and a reference an operator
 * can find — and a member who sees no house account today can still file one,
 * which some brands rely on because they hand the account out elsewhere.
 * Blocking here would stop money arriving for them.
 */
export function depositModeBlocker(
  mode: DepositMode,
  ctx: DepositModeContext,
): DepositModeBlocker | null {
  if (mode === 'slip') return ctx.slipStorageUnavailable ? 'noSlipStorage' : null;
  if (mode === 'auto') return ctx.houseAccountCount === 0 ? 'noHouseAccount' : null;
  return null;
}

/**
 * The part of a house account this module needs: which deposit flow the
 * operator put it on.
 *
 * OPTIONAL, and absent is not "none". This SPA ships from Cloudflare Pages on
 * its own cadence and can be newer than svc-finance, so a reply written before
 * the column existed carries no channel at all — reading that as "belongs to no
 * flow" would empty every deposit tab for the length of that window, which is
 * exactly what the column's `DEFAULT 'any'` exists to prevent.
 */
export interface ChannelledAccount {
  depositChannel?: string | null;
}

/**
 * The accounts a given flow may point a player at.
 *
 * 'any' and an absent value are every flow. Anything else must match the flow
 * exactly — including a value this build has never heard of, which belongs to a
 * flow it cannot render and must therefore not offer here. Getting that
 * backwards would print an account number under a tab the operator does not
 * reconcile against, and the player's transfer would land with nobody watching
 * for it.
 */
export function banksForMode<T extends ChannelledAccount>(banks: T[], mode: DepositMode): T[] {
  return banks.filter((b) => {
    const channel = b.depositChannel;
    return !channel || channel === 'any' || channel === mode;
  });
}
